import React, { useState } from "react";
import Modal from "react-modal";
import ImageUploading from "react-images-uploading";
import { toast } from "react-toastify";
import useCloudFrontUpload from "../../hooks/useCloudFrontUpload";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { useDispatch } from "react-redux";
import "./VendorImageUploadModal.css";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";

const VendorImageUploadModal = ({
  isOpen,
  onClose,
  expenseId,
  expenseRow,
  userInfoData,
  onSuccess,
}) => {
  const [vendorImages, setVendorImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();
  const dispatch = useDispatch();

  const handleClose = () => {
    setVendorImages([]);
    onClose();
  };

  const onChangeVendorImages = (imageList) => {
    setVendorImages(imageList);
  };

  const handleSubmit = async () => {
    if (vendorImages.length === 0) {
      toast.error("Please select an image to upload");
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload image to CloudFront
      const imageUrl = await uploadToCloudfront(
        vendorImages[0].file,
        userInfoData?.token
      );

      if (!imageUrl) {
        toast.error("Failed to upload image");
        setIsSubmitting(false);
        return;
      }

      // Call vendor payment API
      const payload = {
        vendor_paid_img: imageUrl,
        expense_id: expenseId,
      };

      const result = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: "/expense/fd/vendor_payment",
          payload,
          token: userInfoData?.token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message ||
            "Vendor payment image uploaded successfully!"
        );
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something went wrong!");
      }
    } catch (error) {
      console.error("Error uploading vendor image:", error);
      toast.error("An error occurred while uploading the image");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Upload Vendor Payment Image"
      ariaHideApp={false}
      //   className="vendor-upload-modal"
      //   overlayClassName="vendor-upload-overlay"
    >
      <div className="createNewCTOModalContainer mod-width-50p">
        {/* Header */}
        <ModalHeadAndClose
          title={`Upload Vendor Payment Image`}
          onClose={handleClose}
        />

        {/* Divider */}
        <div className="vendor-upload-divider"></div>

        {/* Content */}
        <div className="vendor-upload-content">
          <div className="vendor-upload-form">
            <label className="vendor-upload-label">
              Upload vendor payment image <span className="required">*</span>
            </label>

            <div className="vendor-upload-area">
              <ImageUploading
                multiple={false}
                value={vendorImages}
                onChange={onChangeVendorImages}
                maxNumber={1}
                dataURLKey="data_url"
                acceptType={["jpg", "png", "jpeg"]}
              >
                {({
                  imageList,
                  onImageUpload,
                  onImageUpdate,
                  onImageRemove,
                  isDragging,
                  dragProps,
                }) => (
                  <div className="vendor-upload-wrapper">
                    {vendorImages?.length === 0 ? (
                      <div
                        className={`vendor-upload-dropzone ${
                          isDragging ? "dragging" : ""
                        }`}
                        onClick={onImageUpload}
                        {...dragProps}
                      >
                        <div className="vendor-upload-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                          >
                            <path
                              d="M24 16V32M16 24H32"
                              stroke="#6B7280"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M42 24C42 34.4934 33.4934 43 23 43C12.5066 43 4 34.4934 4 24C4 13.5066 12.5066 5 23 5C33.4934 5 42 13.5066 42 24Z"
                              stroke="#6B7280"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                        <div className="vendor-upload-text">
                          <p className="vendor-upload-main-text">
                            {isDragging
                              ? "Drop the image here"
                              : "Click to upload or drag and drop"}
                          </p>
                          <p className="vendor-upload-sub-text">
                            PNG, JPG up to 10MB
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="vendor-upload-preview">
                        {imageList.map((image, index) => (
                          <div key={index} className="vendor-image-item">
                            <div className="vendor-image-container">
                              <img
                                src={image["data_url"]}
                                alt="Vendor payment"
                                className="vendor-preview-image"
                              />
                              <div className="vendor-image-overlay">
                                <button
                                  type="button"
                                  className="vendor-image-btn update"
                                  onClick={() => onImageUpdate(index)}
                                  title="Change image"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                    <path
                                      fillRule="evenodd"
                                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="vendor-image-btn remove"
                                  onClick={() => onImageRemove(index)}
                                  title="Remove image"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <p className="vendor-image-name">
                              {image.file?.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </ImageUploading>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="vendor-upload-footer">
          <button
            type="button"
            className="vendor-btn vendor-btn-cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="vendor-btn vendor-btn-upload"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading || vendorImages.length === 0}
          >
            {isSubmitting || isUploading ? (
              <div className="vendor-loader">
                <div className="vendor-spinner"></div>
                Uploading...
              </div>
            ) : (
              "Upload Image"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VendorImageUploadModal;
